import type { ReactNode } from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';
import type { RenderSuggestionsContainerParams } from 'react-autosuggest';

import { Box } from '#/components/Box/Box';
import { Image } from '#/components/Image/Image';
import { LoadingSection } from '#/components/LoadingSection/LoadingSection';
import { getStaticImageSrc } from '#/utils/getImageSrc';

type Props = {
  containerProps: RenderSuggestionsContainerParams['containerProps'];
  children: ReactNode;
  listboxId: string;
  loading: boolean;
};

function markNestedListboxesPresentational(container: HTMLDivElement | null) {
  if (!container) return;

  for (const nestedListbox of container.querySelectorAll('[role="listbox"]')) {
    if (nestedListbox !== container) {
      nestedListbox.setAttribute('role', 'presentation');
    }
  }
}

export function SuggestionsContainer({
  containerProps: { id, ref, key, className, role },
  children,
  listboxId,
  loading,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      markNestedListboxesPresentational(node);

      if (typeof ref === 'function') {
        const setAutosuggestRef = ref as (node: HTMLDivElement | null) => void;
        setAutosuggestRef(node);
      }
    },
    [ref],
  );

  useLayoutEffect(() => {
    markNestedListboxesPresentational(containerRef.current);
  }, [children, loading]);

  return (
    <div
      id={id}
      ref={setContainerRef}
      key={key}
      className={className}
      data-loading={loading}
      role={role}
    >
      {loading ? (
        <div
          id={`${listboxId}-loading-option`}
          aria-disabled="true"
          aria-selected="false"
          // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
          role="option"
        >
          <LoadingSection noDelay />
        </div>
      ) : (
        children
      )}
      <Box display="flex" justifyContent="flex-end" mb={8} mr={18}>
        {!loading ? (
          <Image
            height={12}
            src={getStaticImageSrc(
              'poweredbygoogle/powered_by_google_on_white.png',
            )}
            srcSet={`${getStaticImageSrc(
              'poweredbygoogle/powered_by_google_on_white.png',
            )}, ${getStaticImageSrc(
              'poweredbygoogle/powered_by_google_on_white@2x.png',
            )} 2x, ${getStaticImageSrc(
              'poweredbygoogle/powered_by_google_on_white@3x.png',
            )} 3x`}
            alt="Powered by Google"
          />
        ) : null}
      </Box>
    </div>
  );
}
